import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import {ThemeProvider, createTheme} from '@mui/material/styles';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {CalendarPicker} from '@mui/x-date-pickers/CalendarPicker';
import {PickersDay} from '@mui/x-date-pickers/PickersDay';

const calendarTheme = createTheme({
  palette: {primary: {main: '#13A3B2'}},
  typography: {
    fontFamily: 'inherit'
  }
});

const calendarMonths = (numberOfMonths) => {
  const firstMonth = moment().startOf('month');

  return Array.from({length: numberOfMonths}, (unused, index) => (
    firstMonth.clone().add(index, 'months')
  ));
};

const toMoment = (date) => {
  if (moment.isMoment(date)) return date;
  return date ? moment(date) : null;
};

const selectedDateForMonth = (selectedDate, month) => {
  const selectedMoment = toMoment(selectedDate);

  if (!selectedMoment) return null;
  return selectedMoment.isSame(month, 'month') ? selectedMoment : null;
};

const handleCalendarKeyDown = (event) => {
  const date = event.target.getAttribute('data-calendar-day');
  if (!date) return;

  const day = moment(date, 'YYYY-MM-DD');
  const nextDay = day.clone();

  switch (event.key) {
    case 'ArrowLeft': nextDay.subtract(1, 'day'); break;
    case 'ArrowRight': nextDay.add(1, 'day'); break;
    case 'ArrowUp': nextDay.subtract(7, 'days'); break;
    case 'ArrowDown': nextDay.add(7, 'days'); break;
    case 'Home': nextDay.startOf('week'); break;
    case 'End': nextDay.endOf('week'); break;
    case 'PageUp': nextDay.subtract(1, 'month'); break;
    case 'PageDown': nextDay.add(1, 'month'); break;
    default: return;
  }

  if (day.isSame(nextDay, 'month')) return;

  // Keep each picker on its fixed month; focus the day in the adjacent picker.
  event.preventDefault();
  event.stopPropagation();
  const nextButton = event.currentTarget.querySelector(
    `[data-calendar-day="${nextDay.format('YYYY-MM-DD')}"]`
  );
  if (nextButton) nextButton.focus();
};

const ProductSearchCalendar = ({
  selectedDate,
  numberOfMonths = 12,
  onDayClick,
  renderDay,
  className
}) => {
  const [focusedMonth, setFocusedMonth] = React.useState(null);
  const months = calendarMonths(numberOfMonths);
  const activeMonth = focusedMonth || (selectedDate && toMoment(selectedDate).format('YYYY-MM'));
  const tabMonth = months.find(month => month.format('YYYY-MM') === activeMonth) || months[0];

  return (
  <ThemeProvider theme={calendarTheme}>
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div
        className={classnames('product-search-calendar', className)}
        onFocusCapture={(event) => {
          const date = event.target.getAttribute('data-calendar-day');
          if (date) setFocusedMonth(moment(date, 'YYYY-MM-DD').format('YYYY-MM'));
        }}
        onKeyDownCapture={handleCalendarKeyDown}>
        <div className="product-search-calendar-weekdays" aria-hidden="true">
          {Array.from({length: 7}, (unused, index) => (
            <span key={index}>{moment().weekday(index).format('dd')}</span>
          ))}
        </div>
        <div className="product-search-calendar-months">
          {months.map((month) => (
            <div className="product-search-calendar-month" key={month.format('YYYY-MM')}>
              <CalendarPicker
                date={selectedDateForMonth(selectedDate, month)}
                defaultCalendarMonth={month}
                minDate={month}
                maxDate={month.clone().endOf('month')}
                onChange={onDayClick}
                openTo="day"
                views={['day']}
                reduceAnimations
                disableHighlightToday
                renderDay={(day, selectedDays, pickersDayProps) => {
                  const {key, ...dayProps} = pickersDayProps;
                  if (dayProps.outsideCurrentMonth) {
                    return <PickersDay key={key} {...dayProps} />;
                  }

                  return (
                    <PickersDay
                      key={key}
                      {...dayProps}
                      tabIndex={month.isSame(tabMonth, 'month') ? dayProps.tabIndex : -1}
                      data-calendar-day={day.format('YYYY-MM-DD')}
                      disabled={false}
                      disableMargin>
                      {renderDay(day)}
                    </PickersDay>
                  );
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </LocalizationProvider>
  </ThemeProvider>
  );
};

ProductSearchCalendar.propTypes = {
  selectedDate: PropTypes.object,
  numberOfMonths: PropTypes.number,
  onDayClick: PropTypes.func.isRequired,
  renderDay: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default ProductSearchCalendar;
